import {
  LAYER_ROOT_KEY,
  conjuntiveNeighborOperators,
  stemmableNeighborOperators,
  audienceBuilderEdgeMap,
} from '#lit/common/constants/builders/audience_builder.js';
import {ChannelMessage, ChannelMessageTag} from '#utils/channel.js';
import type {
  ClassicBuilderStoreEdgeFactoryParams,
  IBuilderEdgeConstructorParams,
  IClassicBuilderNode,
} from '#builder';
import {BuilderEdgeTypes} from '#builder';
import {NeighborOperators} from '#lit/types/builders/audience_builder/index.js';
import {isFilter, isFlex, isStem} from '#lit/utils/audience_builder/filters.js';
import type {ClassicEdgeUpdatePayload} from '#builder/classic_builder/edge.js';
import {ClassicBuilderEdge} from '#builder/classic_builder/edge.js';
import type {AudienceBuilder} from '#lit/builders/audience_builder/builder.js';
import {
  getChildCount,
  getParents,
  isStemmableNeighbor,
  isStemmableNode,
} from '#lit/utils/audience_builder/nodes.js';
import {v4} from 'uuid';
import {isUndefined} from '#lit/utils/index.js';
import type {DatasetBuilder} from '#lit/builders/dataset_builder/builder.js';

type BuilderImplementation = AudienceBuilder | DatasetBuilder;

type OperatorPredicate = (
  source: IClassicBuilderNode,
  target: IClassicBuilderNode,
  builder: BuilderImplementation,
  opts?: {
    nestLimit: number;
  }
) => boolean;

export const filterOperators = [
  NeighborOperators.AND,
  NeighborOperators.OR,
  NeighborOperators.AND_NOT,
];

type NeighborEdgeConstructorParams = Omit<
  IBuilderEdgeConstructorParams,
  'type'
> & {
  builder?: BuilderImplementation;
};

export const stemOperators = [NeighborOperators.OR];

export enum NeighborEdgeEvents {
  OPERATOR_CHANGE = 'operator-change',
}

export interface NeighborMetadata {
  [k: string]: unknown;
  operator?: NeighborOperators;
  negate?: boolean;
}

export type NeighborEdgeMetadataMutation = (
  ctx: NeighborMetadata | undefined
) => NeighborMetadata;

const stemmableOperatorPredicate: (
  op: NeighborOperators
) => OperatorPredicate = op => {
  /**
   * Determines whether or not a given target is a child filter
   * of a root flex node.
   */
  function isRootFlexChild(
    source: IClassicBuilderNode,
    target: IClassicBuilderNode,
    parents: IClassicBuilderNode[]
  ): boolean {
    const [first] = parents;
    return (
      first &&
      isFlex(first) &&
      parents.length === 1 &&
      isFilter(target) &&
      (!source || isFilter(source))
    );
  }

  /**
   * Determines whether or not a given target is parentless.
   */
  function isParentless(
    target: IClassicBuilderNode,
    builder: BuilderImplementation
  ): boolean {
    const parents = getParents(target, builder);
    return isUndefined(parents[0]);
  }

  /**
   * Determines whether or not the source is a stem and
   * is within the AudienceBuilder's nest limit.
   */
  function isValidStemSource(
    target: IClassicBuilderNode,
    source: IClassicBuilderNode,
    builder: BuilderImplementation,
    parents: IClassicBuilderNode[]
  ) {
    if (!isStem(source)) return false;
    const targetChildCount = isStem(target)
      ? getChildCount(target, builder)
      : 0;
    const sourceChildCount = getChildCount(source, builder);

    return (
      sourceChildCount + parents.length < builder.nestLimit &&
      targetChildCount + parents.length < builder.nestLimit
    );
  }

  return (source, target, builder) => {
    const parents = getParents(target, builder);
    const targetChildCount = isStem(target)
      ? getChildCount(target, builder)
      : 0;
    const [first] = parents;

    // TODO(joshua): Remove after advanced joins is globally enabled
    if (isRootFlexChild(source, target, parents)) {
      return stemmableNeighborOperators.includes(op);
    }

    if (isValidStemSource(target, source, builder, parents)) {
      return !first
        ? isStemmableNeighbor(target, source) ||
            conjuntiveNeighborOperators.includes(op)
        : isStemmableNeighbor(target, source);
    }

    if (isParentless(target, builder)) {
      return (
        (isStemmableNode(target) &&
          stemmableNeighborOperators.includes(op) &&
          isStemmableNeighbor(source, target)) ||
        conjuntiveNeighborOperators.includes(op)
      );
    }

    /** If somehow the parent isn't a stem (which should be impossible, return `false` */
    if (!isStem(first)) return false;

    if (parents.length + targetChildCount < builder.nestLimit) {
      return true;
    }

    return isStem(first) ? op === first.operatorConstraint : false;
  };
};

const operatorPredicateMap = new Map<NeighborOperators, OperatorPredicate>([
  [NeighborOperators.OR, stemmableOperatorPredicate(NeighborOperators.OR)],
  [NeighborOperators.AND, stemmableOperatorPredicate(NeighborOperators.AND)],
  // FIXME(joshua): Replace this with proper negations.
  [
    NeighborOperators.AND_NOT,
    (_, target) => {
      return target.level === LAYER_ROOT_KEY && !isStem(target);
    },
  ],
]);

export class NeighborEdge extends ClassicBuilderEdge<NeighborMetadata> {
  readonly type = BuilderEdgeTypes.FORWARD;
  _operator?: NeighborOperators; // For use with filters
  _negate?: boolean; // For use with filters

  readonly internalId = v4();

  builder?: BuilderImplementation;

  constructor({builder, id, source, target}: NeighborEdgeConstructorParams) {
    super({id, source, target, type: BuilderEdgeTypes.FORWARD});
    if (builder) this.builder = builder;
  }

  get metadata(): NeighborMetadata {
    return {
      operator: this.operator,
      negate: this.negate,
    };
  }

  get operator() {
    return this._operator;
  }

  set operator(op: NeighborOperators | undefined) {
    this._operator = op;

    const tag = ChannelMessageTag.create<
      NeighborEdgeEvents,
      ClassicEdgeUpdatePayload<NeighborEdge>
    >(NeighborEdgeEvents.OPERATOR_CHANGE, {edge: this});

    const message = ChannelMessage.fromTags([tag]);
    this.emit(message);
    this.emitUpdateEvent<NeighborEdge>();
  }

  get negate() {
    return this._negate;
  }

  getSourceNode(): IClassicBuilderNode | void {
    return this.builder?.getNodeFromCache(this.source());
  }

  getTargetNode(): IClassicBuilderNode | void {
    return this.builder?.getNodeFromCache(this.target());
  }

  // TODO(joshua): Remove opts altogether once advanced joins is merged.
  validOperators(
    _opts: {allowFlexOrs: boolean} = {allowFlexOrs: false}
  ): NeighborOperators[] {
    const target = this.getTargetNode();
    const source = this.getSourceNode();

    if (!target || !source) return [];

    if (!this.builder) {
      throw Error('There was an issue accessing the builder instance.');
    }

    // TODO(joshua): Memoize
    return Object.values(NeighborOperators).filter(
      op =>
        !!operatorPredicateMap.get(op)?.(
          source,
          target,
          this.builder as BuilderImplementation
        )
    );
  }

  updateOperator(
    operator: NeighborOperators,
    opts: {allowFlexOrs: boolean} = {allowFlexOrs: true}
  ) {
    if (
      !this.validOperators({allowFlexOrs: opts.allowFlexOrs}).includes(operator)
    ) {
      return;
    }

    this.operator = operator;
    this.emitUpdateEvent<NeighborEdge>();
  }

  updateNegate(negate: boolean) {
    this._negate = negate;
    this.emitUpdateEvent<NeighborEdge>();
  }

  static shouldDisplayOperator(
    source: IClassicBuilderNode,
    target: IClassicBuilderNode
  ): boolean {
    return isStemmableNode(source) && isStemmableNode(target);
  }

  static createEdgeId(source: string, target: string): string {
    return `${source}:${BuilderEdgeTypes.FORWARD}:${target}`;
  }

  static fromStore(params: ClassicBuilderStoreEdgeFactoryParams): NeighborEdge {
    const id = NeighborEdge.createEdgeId(params.source, params.target);
    const metadata = params.metadata as NeighborMetadata;
    const edge = new NeighborEdge({
      id,
      source: params.source,
      target: params.target,
      builder: params.builder as BuilderImplementation,
    });

    if (metadata.operator) {
      edge.updateOperator(metadata.operator);
    }

    if (metadata.negate) {
      edge.updateNegate(metadata.negate);
    }

    return edge;
  }

  static create(
    source: string,
    target: string,
    builder: BuilderImplementation
  ): NeighborEdge {
    const id = NeighborEdge.createEdgeId(source, target);

    const edge = new NeighborEdge({builder, id, source, target});
    return edge;
  }
}

// Add edge class to edge map.
audienceBuilderEdgeMap.set(BuilderEdgeTypes.FORWARD, NeighborEdge);
